import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { getCurrentProfile } from '../features/auth/authService';
import { AppUser } from '../types';

interface AuthContextType {
  session: Session | null;
  profile: AppUser | null;
  isLoading: boolean; // Refers to the Supabase Auth session check
  isProfileSyncing: boolean; // Refers to the background DB profile fetch
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileSyncing, setIsProfileSyncing] = useState(false);
  const initHandshakeRef = useRef<boolean>(false);

  const fetchProfile = useCallback(async (retryCount = 0) => {
    setIsProfileSyncing(true);
    try {
      const data = await getCurrentProfile();
      
      // If we are authenticated but the profile is still 'virtual' and we just signed in,
      // it means the trigger might still be running. Retry up to 3 times.
      if (data?.id.startsWith('virtual-') && retryCount < 3) {
        setTimeout(() => fetchProfile(retryCount + 1), 1500);
        return;
      }
      
      setProfile(data);
    } catch (err) {
      console.error("AuthContext: Profile Sync Error", err);
      setProfile(null);
    } finally {
      setIsProfileSyncing(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      if (initHandshakeRef.current) return;
      initHandshakeRef.current = true;

      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        
        if (isMounted) {
          setSession(initialSession);
          setIsLoading(false);
          if (initialSession) fetchProfile();
        }
      } catch (err) {
        console.error("Auth Init Error:", err);
        if (isMounted) setIsLoading(false);
      }
    };

    initialize();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      if (!isMounted) return;
      
      setSession(currentSession);
      if (currentSession) {
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          fetchProfile();
        }
        setIsLoading(false);
      } else {
        setProfile(null);
        setIsLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  return (
    <AuthContext.Provider value={{ session, profile, isLoading, isProfileSyncing, refreshProfile: () => fetchProfile() }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};